import { supabase } from './supabase';

// Agents V2: fresh agent system (receptionist first). Independent of the
// legacy ai_agents stack.

export type AgentV2Kind = 'receptionist' | 'feedback';
export type AgentV2Status = 'draft' | 'live';

export type AnswerHoursDay = {
  enabled?: boolean;
  start?: string; // "HH:MM" 24h
  end?: string;   // "HH:MM" 24h — end earlier than start runs overnight
};

export type AnswerMode = 'always' | 'missed_calls' | 'after_hours';

export type VoiceEngine = 'elevenlabs' | 'openai';

export type GreetingMode = 'auto' | 'guided' | 'exact';
export type Persona = 'ai' | 'human';
/** Only 'simple_callback' exists today; a full agent (bookings, support dispatch) comes later. */
export type CallGoal = 'simple_callback';

/**
 * Per-source data access toggles. Everything defaults to OFF: with no access
 * the receptionist is a pure callback tool that knows nothing about callers.
 */
export type ReceptionistAccess = {
  customer_profile?: boolean;
  vehicles?: boolean;
  bookings?: boolean;
  invoices?: boolean;
  proposals?: boolean;
  crm_leads?: boolean;
  messages?: boolean;
  call_history?: boolean;
  memberships?: boolean;
  gift_cards?: boolean;
  projects?: boolean;
  pricing_details?: boolean;
  service_areas?: boolean;
};

export type AccessKey = keyof ReceptionistAccess;

export type ReceptionistConfig = {
  greeting?: string;
  /** 'exact' = say greeting verbatim (default); 'guided' = improvise following greeting as a guideline; 'auto' = improvise freely each call. */
  greeting_mode?: GreetingMode;
  /** Which stack speaks on calls. 'openai' = speech-to-speech Realtime (most human); 'elevenlabs' = ConversationRelay TTS. */
  voice_engine?: VoiceEngine;
  /** OpenAI Realtime voice name (marin, cedar, coral, ...). Used when voice_engine is 'openai'. */
  openai_voice?: string;
  /** ElevenLabs voice ID passed to ConversationRelay. */
  voice?: string;
  voice_gender?: 'male' | 'female';
  /** 0..1, default 0.6. Higher = more emotional variation on calls (lower ElevenLabs stability). */
  expressiveness?: number;
  /** Talking pace, 0.7..1.2 (ConversationRelay limits). Default 1.0. */
  speed?: number;
  /** ElevenLabs similarity 0.3..1. Higher = crisp studio fidelity, lower = looser and more natural. Default 0.75. */
  polish?: number;
  /** 'ai' (default) = polished virtual receptionist; 'human' = tuned to be indistinguishable from a real person. */
  persona?: Persona;
  /** How the receptionist words things on calls (prompt-side). */
  energy?: 'calm' | 'balanced' | 'upbeat';
  filler_style?: 'off' | 'subtle' | 'chatty';
  language?: string;
  custom_instructions?: string;
  // 'no_forward' is a legacy alias for 'missed_calls'
  answer_mode?: AnswerMode | 'no_forward';
  answer_hours?: Record<string, AnswerHoursDay>;
  // missed_calls mode: how long your phone rings before the AI answers.
  // Must be shorter than your cell voicemail's pickup time.
  ring_seconds?: number;
  call_goal?: CallGoal;
  /** Info items to collect for the callback (natural-language labels, e.g. "name", "phone number", "email"). */
  collect?: string[];
  /** Per-source data access toggles. All off by default. */
  access?: ReceptionistAccess;
  /** Assigned business profile (business_profiles.id) grounding the agent in the organization. */
  business_profile_id?: string;
  /** Feedback agent: what happens after a 5-star rating. The review link itself is the shared businesses.google_review_url. */
  five_star_action?: 'none' | 'google_review';
  /** Feedback agent: below-5 action ('support_ticket' not built yet). */
  low_star_action?: 'none' | 'support_ticket';
};

export function normalizeAnswerMode(mode: string | undefined): AnswerMode {
  if (mode === 'missed_calls' || mode === 'no_forward') return 'missed_calls';
  if (mode === 'after_hours') return 'after_hours';
  return 'always';
}

export type AgentV2 = {
  id: string;
  business_id: string;
  kind: AgentV2Kind;
  name: string;
  status: AgentV2Status;
  config: ReceptionistConfig;
  created_at: string;
  updated_at: string;
};

export type AgentCallSession = {
  id: string;
  agent_id: string | null;
  business_id: string;
  call_sid: string | null;
  from_number: string | null;
  to_number: string | null;
  transcript: Array<{ role: 'user' | 'assistant'; text: string; ts?: string }>;
  summary: string | null;
  outcome: string | null;
  collected: { name?: string; phone?: string; reason?: string } | null;
  /** Set when the caller-context lookup partially/fully failed on this call (call still proceeded). */
  context_error: string | null;
  lead_opportunity_id: number | null;
  duration_seconds: number | null;
  started_at: string;
  ended_at: string | null;
};

export async function listAgents(businessId: string): Promise<AgentV2[]> {
  const { data, error } = await supabase
    .from('agents_v2')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as AgentV2[];
}

export async function getAgent(agentId: string): Promise<AgentV2 | null> {
  const { data, error } = await supabase
    .from('agents_v2')
    .select('*')
    .eq('id', agentId)
    .maybeSingle();
  if (error) throw error;
  return (data as AgentV2) || null;
}

export async function createAgent(
  businessId: string,
  kind: AgentV2Kind,
  name: string,
): Promise<AgentV2> {
  const { data, error } = await supabase
    .from('agents_v2')
    .insert({ business_id: businessId, kind, name, status: 'draft', config: {} })
    .select('*')
    .single();
  if (error) throw error;
  return data as AgentV2;
}

export async function updateAgent(
  agentId: string,
  patch: Partial<Pick<AgentV2, 'name' | 'config'>>,
): Promise<AgentV2> {
  const { data, error } = await supabase
    .from('agents_v2')
    .update(patch)
    .eq('id', agentId)
    .select('*')
    .single();
  if (error) throw error;
  return data as AgentV2;
}

/**
 * Goes live / back to draft. Only one live agent per kind per business is
 * allowed (DB unique index), so going live demotes any other live agent of
 * the same kind first.
 */
export async function setAgentStatus(
  agent: Pick<AgentV2, 'id' | 'business_id' | 'kind'>,
  status: AgentV2Status,
): Promise<void> {
  if (status === 'live') {
    const { error: demoteError } = await supabase
      .from('agents_v2')
      .update({ status: 'draft' })
      .eq('business_id', agent.business_id)
      .eq('kind', agent.kind)
      .eq('status', 'live')
      .neq('id', agent.id);
    if (demoteError) throw demoteError;
  }
  const { error } = await supabase
    .from('agents_v2')
    .update({ status })
    .eq('id', agent.id);
  if (error) throw error;
}

export async function deleteAgent(agentId: string): Promise<void> {
  const { error } = await supabase.from('agents_v2').delete().eq('id', agentId);
  if (error) throw error;
}

export async function listAgentSessions(
  agentId: string,
  limit = 50,
): Promise<AgentCallSession[]> {
  const { data, error } = await supabase
    .from('agent_call_sessions')
    .select('*')
    .eq('agent_id', agentId)
    .order('started_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []) as AgentCallSession[];
}

export async function simulateAgentTurn(
  agentId: string,
  messages: Array<{ role: 'user' | 'assistant'; text: string }>,
): Promise<{ reply: string; is_greeting?: boolean }> {
  const { data, error } = await supabase.functions.invoke('agent-simulate', {
    body: { agent_id: agentId, messages },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data as { reply: string; is_greeting?: boolean };
}
